import {ethers, JsonRpcApiProvider, Network} from "ethers";
import {DateTime} from "luxon";
import * as utils from "@/utils";
import * as constants from "@/constants";
import * as customTokensService from "@/services/customTokensService";
import * as privateKeyWalletService from "@/services/privateKeyWalletService";
import _ from "lodash";
import axios from "@/axios";
import BigNumber from "bignumber.js";
import type {PhantomProvider} from "@/types";
import * as onboardComposableExtra from "@/services/onboard";

import bs58 from "bs58";
import {AddressLookupTableAccount, Connection, Keypair, PublicKey, Transaction, VersionedTransaction} from "@solana/web3.js";
import { ed25519 } from '@noble/curves/ed25519';
import {createUmi} from "@metaplex-foundation/umi-bundle-defaults";
import {deserializeMetadata, findMetadataPda} from "@metaplex-foundation/mpl-token-metadata";
import type {RpcAccount} from "@metaplex-foundation/umi";
import {publicKey} from "@metaplex-foundation/umi";
import {MintLayout, TOKEN_PROGRAM_ID, TOKEN_2022_PROGRAM_ID} from "@solana/spl-token";

import erc20Abi from "./abi/erc20Abi.json";
import multiCall3Abi from "./abi/multiCall3Abi.json";

const erc20Interface = new ethers.Interface(erc20Abi);
const multiCall3Interface = new ethers.Interface(multiCall3Abi);

const web3ProviderByChain = Object.keys(constants.evmRpcUrlByChain).reduce((ret, chainId) => {
  const network = Network.from(+chainId);
  ret[chainId] = new ethers.JsonRpcProvider(
    constants.evmRpcUrlByChain[chainId],
    network,
    { staticNetwork: network, batchMaxCount: 1 }
  );
  return ret;
}, {} as Record<number, ethers.JsonRpcProvider>);

const cachedTokens = {};
// chainId -> walletAddress -> tokenAddress -> balance
const cachedWalletBalances: Record<string, Record<string, Record<string, string>>> = {};

let cachedTronWalletAddress;

let currentSolanaWalletProvider: PhantomProvider;

export const solanaProviderGetters: Record<string, () => PhantomProvider> = {
  phantom: () => window.phantom?.solana,
  solflare: () => window.solflare,
  backpack: () => window.backpack,
  okx: () => window.okxwallet?.solana
};

export const solanaWeb3 = new Connection("https://melony-h3b322-fast-mainnet.helius-rpc.com/");
export const umi = createUmi(solanaWeb3.rpcEndpoint);

export function getBrowserProvider(): ethers.BrowserProvider {
  return onboardComposableExtra.browserProvider.value;
}

export function solanaPrivateKeyWalletProvider(bs58PrivateKey: string): PhantomProvider {
  const keypair = Keypair.fromSecretKey(bs58.decode(bs58PrivateKey));
  return {
    async connect() {
      return { publicKey: keypair.publicKey };
    },
    async disconnect() {},
    isConnected: true,
    publicKey: keypair.publicKey,
    async request() {},
    on() {},
    once() {},
    off() {},
    async signTransaction<T extends Transaction | VersionedTransaction>(transaction: T) {
      if (transaction instanceof VersionedTransaction) {
        transaction.sign([keypair]);
      } else {
        transaction.sign(keypair);
      }
      return transaction;
    },
    async signAndSendTransaction() { return null; },
    async signAllTransactions<T extends Transaction | VersionedTransaction>(transactions: T[]) {
      return Promise.all(transactions.map(this.signTransaction));
    },
    async signMessage(message: Uint8Array | string, display?: 'utf8' | 'hex') {
      ed25519.sign(message, keypair.secretKey);
    }
  };
}

export async function getSolanaProvider(walletAddress?: string, requestBrowserProvider = false): Promise<PhantomProvider> {
  if (requestBrowserProvider) {
    currentSolanaWalletProvider = await waitForSolanaBrowserProvider(AbortSignal.timeout(3000));
    return currentSolanaWalletProvider;
  }

  if (walletAddress) {
    const privateKeyWallets = privateKeyWalletService.getWallets().filter(wallet => wallet.type === "solana");
    const walletObj = privateKeyWallets.find(w => w.address === walletAddress);
    if (walletObj) {
      currentSolanaWalletProvider = solanaPrivateKeyWalletProvider(walletObj.privateKey);
    } else if (walletAddress !== currentSolanaWalletProvider?.publicKey?.toString()) {
      throw new Error("no Solana provider found for " + walletAddress);
    }
  }

  if (!currentSolanaWalletProvider) {
    try {
      currentSolanaWalletProvider = await waitForSolanaBrowserProvider(AbortSignal.timeout(3000));
    } catch (e) {
      const privateKeyWallets = privateKeyWalletService.getWallets().filter(wallet => wallet.type === "solana");
      const privateKeyWallet = privateKeyWallets[0];
      if (privateKeyWallet) {
        currentSolanaWalletProvider = solanaPrivateKeyWalletProvider(privateKeyWallet.privateKey);
      }
    }
  }

  return currentSolanaWalletProvider;
}

export async function waitForSolanaBrowserProvider(abortSignal?: AbortSignal) {
  let lastUsedKey = localStorage.getItem("solanaInjectedWallet");
  if (!Object.keys(solanaProviderGetters).includes(lastUsedKey)) {
    lastUsedKey = null;
  }

  while(!(abortSignal?.aborted)) {
    const providers = getAllSolanaInjectedProviders();
    for (const key in providers) {
      const provider = providers[key];
      if (provider) {
        if (lastUsedKey === key) {
          return provider;
        }
        if (!lastUsedKey) {
          localStorage.setItem("solanaInjectedWallet", key);
          return provider;
        }
      }
    }
    await utils.delay(50);
  }
  throw new Error("abortSignal: aborted");
}

export function getAllSolanaInjectedProviders() {
  const ret: Record<string, PhantomProvider> = {};
  for (const key in solanaProviderGetters) {
    const provider = solanaProviderGetters[key]();
    if (provider) {
      ret[key] = provider;
    }
  }
  return ret;
}

export async function getProviderForChain(chainId: number): Promise<JsonRpcApiProvider> {
  return web3ProviderByChain[chainId];
}

export function getTronWeb() {
  return window.tronWeb;
}

export async function getConnectedEvmAddress(): Promise<string | undefined> {
  return onboardComposableExtra.walletAddress.value;
}


export async function getConnectedTronAddress() {
  if (getTronWeb().defaultAddress.base58) {
    return getTronWeb().defaultAddress.base58;
  } else {
    console.log("web3Service tron_requestAccounts");
    await getTronWeb().request({ method: "tron_requestAccounts" });
    return getTronWeb().defaultAddress.base58;
  }
}

export async function getConnectedEvmChainId(): Promise<number> {
  return +onboardComposableExtra.chainId.value;
}

export async function getContractAttrs(chainId: number, address: string, abi: any[]) {
  const readFunctions = abi.filter(fragmentAbi =>
    fragmentAbi.type === "function" &&
    fragmentAbi.inputs.length === 0 &&
    ["pure", "view"].includes(fragmentAbi.stateMutability)
  );
  const contractInterface = new ethers.Interface(abi);
  const getLbpInfoCalls = readFunctions.map(functionAbi => ({
    target: address,
    callData: contractInterface.encodeFunctionData(functionAbi.name)
  }));

  const multiCall3Contract = new ethers.Contract(
    constants.MULTICALL3_CONTRACT[chainId],
    multiCall3Interface,
    await getProviderForChain(chainId)
  );

  const getLbpInfoMultiCallRet = await multiCall3Contract.tryAggregate(false, getLbpInfoCalls);

  const ret: Record<string, any> = {};
  for (let i = 0; i < getLbpInfoCalls.length; i++) {
    if (getLbpInfoMultiCallRet[i][0]) {
      const name = readFunctions[i].name;
      const decoded = contractInterface.decodeFunctionResult(name, getLbpInfoMultiCallRet[i][1]);
      ret[name] = decoded.length === 1 ? decoded[0] : decoded;
    }
  }

  return ret;
}


type GetTokensReturnType = Record<string, { address: string, symbol: string, name: string, decimals: number, logoURI?: string, tags?: string[] }>;
export async function getListedTokens(chainId: number): Promise<GetTokensReturnType> {
  if (cachedTokens[chainId]) {
    return cachedTokens[chainId];

  } else {
    const fetchListedTokensPromise = fetchListedTokens(chainId);
    try {
      const fromLocalStorage = JSON.parse(localStorage.getItem("listedTokens-" + chainId));
      if (DateTime.now().diff(DateTime.fromISO(fromLocalStorage.ts)).as("days") < 1) {
        return fromLocalStorage.tokens;
      }
    } catch (e) {}
    return fetchListedTokensPromise;
  }
}

async function fetchListedTokens(chainId: number): Promise<GetTokensReturnType> {
  const fetchBinanceAlphaTokens = axios.get("https://www.binance.com/bapi/defi/v1/public/wallet-direct/buw/wallet/cex/alpha/all/token/list");

  if (utils.isEvmChain(chainId)) {
    cachedTokens[chainId] = (await axios.get(`https://tokens.1inch.io/v1.1/${chainId}`)).data;

  } else if (chainId === constants.CHAIN_ID_SOLANA) {
    const tokens = (await axios.get("https://lite-api.jup.ag/tokens/v2/tag?query=verified")).data.map(t => ({
      address: t.id,
      symbol: t.symbol,
      name: t.name,
      decimals: t.decimals,
      logoURI: t.icon,
      tokenProgram: t.tokenProgram,
    }));
    const tokensKeyedByMint = _.keyBy(tokens, "address");
    tokensKeyedByMint[constants.WSOL].name = "Solana";
    cachedTokens[chainId] = tokensKeyedByMint;

  } else if (chainId === constants.CHAIN_ID_TRON) {
    const tokens = (await axios.get("https://list.justswap.link/justswap.json")).data.tokens;
    for (const token of tokens) {
      token.chainId = constants.CHAIN_ID_TRON; // fix chainId
    }
    const tokensKeyed = _.keyBy(tokens, "address");
    tokensKeyed[constants.ZERO_ADDRESS_TRON] = {
      address: constants.ZERO_ADDRESS_TRON,
      symbol: "TRX",
      name: "Tron",
      decimals: 6,
      logoURI: constants.NETWORK_LOGO[constants.CHAIN_ID_TRON]
    };
    cachedTokens[chainId] = tokensKeyed;
  }

  const binanceAlphaTokens = (await fetchBinanceAlphaTokens).data.data;
  for (const binanceAlphaToken of binanceAlphaTokens) {
    if (+binanceAlphaToken.chainId === chainId) {
      const key = utils.isEvmChain(chainId) ? binanceAlphaToken.contractAddress.toLowerCase() : binanceAlphaToken.contractAddress;
      cachedTokens[chainId][key] = {
        address: binanceAlphaToken.contractAddress,
        symbol: binanceAlphaToken.symbol,
        name: binanceAlphaToken.name,
        decimals: binanceAlphaToken.decimals,
        logoURI: binanceAlphaToken.iconUrl.replace("https://bin.bnbstatic.com/", "https://arb-trader.coinmatic.app/api/proxy/bin.bnbstatic.com/")
      };
    }
  }

  localStorage.setItem("listedTokens-" + chainId, JSON.stringify({
    tokens: cachedTokens[chainId],
    ts: new Date().toISOString(),
  }));
  return cachedTokens[chainId];
}

export async function getEvmTokenMetadata(chainId: number, tokenAddress: string): Promise<{ name: string, symbol: string, decimals: number }> {
  try {
    const multiCall3Contract = new ethers.Contract(
      constants.MULTICALL3_CONTRACT[chainId],
      multiCall3Interface,
      await getProviderForChain(chainId)
    );
    const calls = [
      { target: tokenAddress, callData: erc20Interface.encodeFunctionData("name") },
      { target: tokenAddress, callData: erc20Interface.encodeFunctionData("symbol") },
      { target: tokenAddress, callData: erc20Interface.encodeFunctionData("decimals") },
    ];
    const multiCallRet = (await multiCall3Contract.aggregate(calls))[1];

    return {
      name: erc20Interface.decodeFunctionResult("name", multiCallRet[0])[0],
      symbol: erc20Interface.decodeFunctionResult("symbol", multiCallRet[1])[0],
      decimals: Number(erc20Interface.decodeFunctionResult("decimals", multiCallRet[2])[0])
    };

  } catch (e) {
    console.error(e);
    return null;
  }
}

export async function getSolanaTokenMetadata(mint: string): Promise<{ name: string, symbol: string, decimals: number, logo: string }> {
  if (mint === constants.WSOL) {
    return { name: "Solana", symbol: "SOL", decimals: 9, logo: null };
  }

  const mintPk = publicKey(mint);
  const metadataPda = findMetadataPda(umi, {
    mint: mintPk
  });
  const metadataPk = publicKey(metadataPda);

  const accounts = await umi.rpc.getAccounts([mintPk, metadataPk]);

  const decodedMintAccount = MintLayout.decode((accounts[0] as RpcAccount).data);
  const ret = { name: null, symbol: null, decimals: decodedMintAccount.decimals, logo: null };

  const metadataAccount = accounts[1];
  if (metadataAccount.exists) {
    const decodedMetadataAccount = deserializeMetadata(metadataAccount);
    ret.symbol = decodedMetadataAccount.symbol;
    ret.name = decodedMetadataAccount.name;
    const uri = decodedMetadataAccount.uri;
    if (uri) {
      try {
        const extraMetadata = await axios.get(uri, { timeout: 3000 });
        const logo = extraMetadata.data?.image;
        if (logo) ret.logo = logo;
      } catch (e) {}
    }
  }

  return ret;
}

export async function getAddressLookupTableAccounts(publicKeys: PublicKey[]): Promise<AddressLookupTableAccount[]> {
  const addressLookupTableAccountInfos = await solanaWeb3.getMultipleAccountsInfo(publicKeys);

  return addressLookupTableAccountInfos.reduce((acc, accountInfo, index) => {
    const addressLookupTableAddress = publicKeys[index];
    if (accountInfo) {
      const addressLookupTableAccount = new AddressLookupTableAccount({
        key: addressLookupTableAddress,
        state: AddressLookupTableAccount.deserialize(accountInfo.data),
      });
      acc.push(addressLookupTableAccount);
    }
    return acc;
  }, []);
}

export async function getTronTokenMetadata(tokenAddress: string): Promise<{ name: string, symbol: string, decimals: number }> {
  try {
    const multiCall3Address = constants.MULTICALL3_CONTRACT[constants.CHAIN_ID_TRON];
    const multiCall3Contract = getTronWeb().contract(multiCall3Abi, multiCall3Address);
    const tokenAddressHex = tronAddressToHex(tokenAddress);
    const calls = [
      [ tokenAddressHex, erc20Interface.encodeFunctionData("name") ],
      [ tokenAddressHex, erc20Interface.encodeFunctionData("symbol") ],
      [ tokenAddressHex, erc20Interface.encodeFunctionData("decimals") ],
    ];
    const multiCallRet = (await multiCall3Contract.aggregate(calls).call())[1];

    return {
      name: erc20Interface.decodeFunctionResult("name", multiCallRet[0])[0],
      symbol: erc20Interface.decodeFunctionResult("symbol", multiCallRet[1])[0],
      decimals: Number(erc20Interface.decodeFunctionResult("decimals", multiCallRet[2])[0])
    };

  } catch (e) {
    console.error(e);
    return null;
  }
}

export async function getEvmTokenBalance(chainId: number, walletAddress: string, tokenAddress: string): Promise<bigint> {
  if (!walletAddress) return 0n;
  const provider = await getProviderForChain(chainId);
  if (tokenAddress.toLowerCase() === constants.NATIVE_ASSET_ADDRESS.toLowerCase()) {
    return provider.getBalance(walletAddress);
  } else {
    const contract = new ethers.Contract(tokenAddress, erc20Abi, provider);
    const balance = await contract.balanceOf(walletAddress);
    return balance;
  }
}

export async function getTronTokenBalances(walletAddress: string, tokenAddresses: string[]): Promise<Record<string, bigint>> {
  if (!walletAddress) return {};

  const walletAddressHex = tronAddressToHex(walletAddress);
  const multiCall3Address = constants.MULTICALL3_CONTRACT[constants.CHAIN_ID_TRON];
  const multiCall3AddressHex = tronAddressToHex(multiCall3Address);

  try {
    const multiCall3Contract = getTronWeb().contract(multiCall3Abi, multiCall3Address);

    const calls = [];
    const applyResultFunctions = [];
    const ret: Record<string, bigint> = {};
    for (const tokenAddress of tokenAddresses) {
      if (tokenAddress === constants.ZERO_ADDRESS_TRON) {
        calls.push([
          multiCall3AddressHex,
          multiCall3Interface.encodeFunctionData("getEthBalance", [walletAddressHex])
        ]);
        applyResultFunctions.push(data => {
          ret[tokenAddress] = multiCall3Interface.decodeFunctionResult("getEthBalance", data)[0];
        });
      } else {
        const tokenAddressHex = tronAddressToHex(tokenAddress);
        calls.push([
          tokenAddressHex,
          erc20Interface.encodeFunctionData("balanceOf", [walletAddressHex])
        ]);
        applyResultFunctions.push(data => {
          ret[tokenAddress] = erc20Interface.decodeFunctionResult("balanceOf", data)[0];
        });
      }
    }

    const multiCallRet = (await multiCall3Contract.tryAggregate(false, calls).call())[0];
    for (let i = 0; i < calls.length; i++) {
      if (multiCallRet[i][0]) {
        applyResultFunctions[i](multiCallRet[i][1]);
      }
    }
    return ret;

  } catch (e) {
    console.error(e);
    return {};
  }
}

export async function getEvmTokenBalances(chainId: number, walletAddress: string, tokenAddresses: string[]): Promise<Record<string, bigint>> {
  if (!walletAddress) return {};

  try {
    const provider = await getProviderForChain(chainId);
    const multiCall3Address = constants.MULTICALL3_CONTRACT[chainId];
    const multiCall3Contract = new ethers.Contract(multiCall3Address, multiCall3Interface, provider);

    const calls = [];
    const applyResultFunctions = [];
    const ret: Record<string, bigint> = {};
    for (const tokenAddress of tokenAddresses) {
      if (tokenAddress.toLowerCase() === constants.NATIVE_ASSET_ADDRESS.toLowerCase()) {
        calls.push({
          target: multiCall3Address,
          callData: multiCall3Interface.encodeFunctionData("getEthBalance", [walletAddress])
        });
        applyResultFunctions.push(data => {
          ret[tokenAddress.toLowerCase()] = multiCall3Interface.decodeFunctionResult("getEthBalance", data)[0];
        });
      } else {
        calls.push({
          target: tokenAddress,
          callData: erc20Interface.encodeFunctionData("balanceOf", [walletAddress])
        });
        applyResultFunctions.push(data => {
          ret[tokenAddress.toLowerCase()] = erc20Interface.decodeFunctionResult("balanceOf", data)[0];
        });
      }
    }

    const multiCallRet = await multiCall3Contract.tryAggregate(false, calls);
    for (let i = 0; i < calls.length; i++) {
      if (multiCallRet[i][0]) {
        applyResultFunctions[i](multiCallRet[i][1]);
      }
    }
    return ret;

  } catch (e) {
    console.error(e);
    return {};
  }
}

export async function getBalancesAndAllowances(chainId: number, walletAddress: string, spender: string, tokenAddresses: string[]) {
  if (!walletAddress) return {};

  try {
    const provider = await getProviderForChain(chainId);
    const multiCall3Address = constants.MULTICALL3_CONTRACT[chainId];
    const multiCall3Contract = new ethers.Contract(multiCall3Address, multiCall3Interface, provider);

    const calls = [];
    const applyResultFunctions = [];
    const ret: Record<string, { balance: bigint, allowance?: bigint }> = {};
    for (const tokenAddress of tokenAddresses) {
      if (tokenAddress.toLowerCase() === constants.NATIVE_ASSET_ADDRESS.toLowerCase()) {
        calls.push({
          target: multiCall3Address,
          callData: multiCall3Interface.encodeFunctionData("getEthBalance", [walletAddress])
        });
        applyResultFunctions.push(data => {
          _.set(ret, [tokenAddress.toLowerCase(), "balance"], multiCall3Interface.decodeFunctionResult("getEthBalance", data)[0]);
        });
      } else {
        calls.push({
          target: tokenAddress,
          callData: erc20Interface.encodeFunctionData("balanceOf", [walletAddress])
        });
        applyResultFunctions.push(data => {
          _.set(ret, [tokenAddress.toLowerCase(), "balance"], erc20Interface.decodeFunctionResult("balanceOf", data)[0]);
        });

        if (spender) {
          calls.push({
            target: tokenAddress,
            callData: erc20Interface.encodeFunctionData("allowance", [walletAddress, spender])
          });
          applyResultFunctions.push(data => {
            _.set(ret, [tokenAddress.toLowerCase(), "allowance"], erc20Interface.decodeFunctionResult("allowance", data)[0]);
          });
        }
      }
    }

    const multiCallRet = await multiCall3Contract.tryAggregate(false, calls);
    for (let i = 0; i < calls.length; i++) {
      if (multiCallRet[i][0]) {
        applyResultFunctions[i](multiCallRet[i][1]);
      }
    }
    return ret;

  } catch (e) {
    console.error(e);
    return {};
  }
}

export function getCachedWalletTokenBalances(chainId: number, address: string) {
  const addressKey = utils.isEvmChain(chainId) ? address.toLowerCase() : address;
  return utils.jsonClone(cachedWalletBalances[chainId]?.[addressKey] || {});
}

export async function getEvmWalletListedAndCustomTokenBalances(chainId: number, address: string) {
  const tokenBalances: Record<string, string> = {};

  if (address && chainId) {
    const fetchListedTokenBalances = axios.get(`https://balances.1inch.io/v1.1/${chainId}/balances/${address}`);

    const customTokenAddresses = customTokensService.getAllCustomTokens(chainId).map(it => it.address);
    const fetchCustomTokenBalances = getEvmTokenBalances(chainId, address, customTokenAddresses);

    const listedTokenBalances = (await fetchListedTokenBalances).data;
    const customTokenBalances = await fetchCustomTokenBalances;

    const balanceObjs = [listedTokenBalances, customTokenBalances];

    for (const balanceObj of balanceObjs) {
      for (const address in balanceObj) {
        const balanceBN = BigNumber(balanceObj[address]);
        if (balanceBN.gt(0)) {
          tokenBalances[address.toLowerCase()] = balanceBN.toFixed();
        }
      }
    }
  }

  _.set(cachedWalletBalances, [chainId, address.toLowerCase()], tokenBalances);

  return getCachedWalletTokenBalances(chainId, address);
}

let maxKnownSolanaSlot = 0;
export async function getSolanaWalletTokenBalances(address: string) {
  const tokenBalances: Record<string, string> = {};

  if (address) {
    const rpcResults = (await axios.post(solanaWeb3.rpcEndpoint, [
      {
        method: "getAccountInfo",
        jsonrpc: "2.0",
        params: [
          address,
          {
            encoding: "base64",
            commitment: "confirmed",
            minContextSlot: maxKnownSolanaSlot
          }
        ],
        id: crypto.randomUUID()
      },
      {
        method: "getTokenAccountsByOwner",
        jsonrpc: "2.0",
        params: [
          address,
          {
            programId: TOKEN_PROGRAM_ID.toString()
          },
          {
            encoding: "jsonParsed",
            commitment: "confirmed",
            minContextSlot: maxKnownSolanaSlot
          }
        ],
        id: crypto.randomUUID()
      },
      {
        method: "getTokenAccountsByOwner",
        jsonrpc: "2.0",
        params: [
          address,
          {
            programId: TOKEN_2022_PROGRAM_ID.toString()
          },
          {
            encoding: "jsonParsed",
            commitment: "confirmed",
            minContextSlot: maxKnownSolanaSlot
          }
        ],
        id: crypto.randomUUID()
      }
    ])).data;

    for (const rpcResult of rpcResults) {
      const slot = rpcResult.result.context.slot;
      if (slot > maxKnownSolanaSlot) {
        maxKnownSolanaSlot = slot;
      }
    }

    for (const account of rpcResults[1].result.value) {
      const info = account.account.data.parsed.info;
      tokenBalances[info.mint] = info.tokenAmount.amount;
      /*const ata = getAssociatedTokenAddressSync(new PublicKey(info.mint), new PublicKey(address));
      const tokenAccountAddress = account.pubkey;
      if (ata.toString() === tokenAccountAddress) {
      }*/
    }
    for (const account of rpcResults[2].result.value) {
      const info = account.account.data.parsed.info;
      tokenBalances[info.mint] = info.tokenAmount.amount;
    }

    // This overrides WSOL balance with SOL balance
    tokenBalances[constants.WSOL] = rpcResults[0].result.value?.lamports || 0;
  }

  _.set(cachedWalletBalances, [constants.CHAIN_ID_SOLANA, address], tokenBalances);

  return getCachedWalletTokenBalances(constants.CHAIN_ID_SOLANA, address);
}



export async function solanaSpamSendTx(transaction: Transaction | VersionedTransaction, times = 3) {
  const serialized = Buffer.from(transaction.serialize()).toString("base64");
  const signature = await solanaWeb3.sendEncodedTransaction(serialized, { skipPreflight: true, maxRetries: 0 });

  (async () => {
    const postBody = JSON.stringify(serialized);
    for (let i = 0; i < times; i++) {
      axios.post("https://worker.jup.ag/send-transaction", postBody, {
        headers: {
          "content-type": "application/json"
        }
      }).then(_.noop).catch(_.noop);
      solanaWeb3.sendEncodedTransaction(serialized, { skipPreflight: true, maxRetries: 0 }).then(_.noop).catch(_.noop);
      await utils.delay(2000);
    }
  })();

  return signature;
}

export async function getTronWalletTokenBalances(address: string) {
  const chainId = constants.CHAIN_ID_TRON;

  const tokenBalances: Record<string, string> = {};

  if (address && chainId) {
    const listedTokens = await getListedTokens(chainId);
    const customTokenAddresses = customTokensService.getAllCustomTokens(chainId).map(it => it.address);

    const allTokenAddresses = _.uniq([
      ...Object.keys(listedTokens),
      ...customTokenAddresses
    ]);

    const balances = await getTronTokenBalances(address, allTokenAddresses);
    for (const tokenAddress in balances) {
      const balance = balances[tokenAddress];
      if (balance > 0) {
        tokenBalances[tokenAddress] = balance.toString();
      }
    }
  }

  _.set(cachedWalletBalances, [chainId, address], tokenBalances);

  return getCachedWalletTokenBalances(chainId, address);
}

export async function getEvmGasPrice(chainId: number) {
  const provider = await getProviderForChain(chainId);
  return ethers.getBigInt(await provider.send("eth_gasPrice", []));
}

export function switchEvmChain(chainId: number) {
  return getBrowserProvider().send("wallet_switchEthereumChain", [{ chainId: "0x" + chainId.toString(16) }]);
}

export function tronAddressToHex(value): string {
  return (window.tronLink?.tronWeb || window.tronWeb).address.toHex(value).replace("41", "0x");
}

export function hexToTronAddress(value): string {
  return (window.tronLink?.tronWeb || window.tronWeb).address.fromHex(value.replace("0x", "41"));
}
